import type { JSX, ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import { pageDe } from '../../../../lib/images.js';
import styles from './Images.module.css';
import reprise from '../Reprise.module.css';

/** Une galerie peut compter des milliers de fichiers : la revue s'affiche par pages. */
export const PAR_PAGE = 20;
/** En deçà, un filtre n'aide pas : la liste se lit d'un coup d'œil. */
const SEUIL_FILTRE = 6;

/**
 * Une liste de la revue, découpée en pages, avec un filtre sur le texte de
 * chaque ligne. Le total affiché est celui de l'API (`total`) ; la liste, elle,
 * peut être tronquée côté API (5 000 entrées au plus).
 */
export function ListePaginee<T>({
  libelle,
  liste,
  total,
  texte,
  rendu,
  vide,
  enLignes = false,
}: {
  libelle: string;
  liste: readonly T[];
  /** Le compteur exact de l'API pour cette liste. */
  total: number;
  /** Le texte sur lequel le filtre porte (chemin, titre…). */
  texte: (ligne: T) => string;
  rendu: (ligne: T) => ReactNode;
  vide: string;
  /** Lignes denses (un tableau sans bordure) plutôt que des fiches. */
  enLignes?: boolean;
}): JSX.Element {
  const { t } = useTranslation('console');
  const [filtre, setFiltre] = useState('');
  const [page, setPage] = useState(1);

  const filtrees = useMemo(() => {
    const mot = filtre.trim().toLowerCase();
    return mot === '' ? liste : liste.filter((ligne) => texte(ligne).toLowerCase().includes(mot));
  }, [filtre, liste, texte]);
  const courante = pageDe(filtrees, page, PAR_PAGE);

  if (total === 0 && liste.length === 0) return <p className={reprise.discret}>{vide}</p>;

  const pagination = (
    <div className={styles.pagination}>
      <Button
        variant="ghost"
        size="sm"
        disabled={courante.page <= 1}
        onClick={() => setPage(courante.page - 1)}
      >
        {t('reprise.depots.manifeste.precedente')}
      </Button>
      <span className={reprise.discret} aria-live="polite">
        {t('reprise.depots.manifeste.page', { page: courante.page, pages: courante.pages })}
      </span>
      <Button
        variant="ghost"
        size="sm"
        disabled={courante.page >= courante.pages}
        onClick={() => setPage(courante.page + 1)}
      >
        {t('reprise.depots.manifeste.suivante')}
      </Button>
    </div>
  );

  return (
    <div className={reprise.pile}>
      <div className={styles.outils}>
        {liste.length > SEUIL_FILTRE ? (
          <div className={styles.filtre}>
            <Input
              type="search"
              aria-label={t('images.liste.filtrer', { liste: libelle })}
              placeholder={t('images.liste.filtre')}
              value={filtre}
              onChange={(evenement) => {
                setFiltre(evenement.target.value);
                setPage(1);
              }}
            />
          </div>
        ) : null}
        {courante.pages > 1 ? pagination : null}
      </div>
      <p className={reprise.discret} aria-live="polite">
        {filtre.trim() === ''
          ? t('images.liste.total', { count: total })
          : t('images.liste.filtrees', { count: filtrees.length, total: liste.length })}
        {liste.length < total ? ` ${t('images.liste.tronquee', { count: liste.length })}` : ''}
      </p>
      {filtrees.length === 0 ? (
        <p className={reprise.discret}>{t('images.liste.aucuneCorrespondance')}</p>
      ) : (
        <ul className={enLignes ? styles.lignes : styles.liste} aria-label={libelle}>
          {courante.lignes.map((ligne) => rendu(ligne))}
        </ul>
      )}
      {courante.pages > 1 ? pagination : null}
    </div>
  );
}
